import { Controller } from "@hotwired/stimulus"
import { Socket } from "phoenix"
import { simpleMarkdownToHtml } from "../helpdesk/src/markdown-utils.js"

// Controller wyszukiwarki KB view — wzorowany na widgecie KB.
// Textarea z 2 przyciskami: "Zapytaj" (AI) i "Szukaj" (submit formularza).
// Bez autocomplete ES — wyszukiwanie dopiero po kliknięciu przycisku.
export default class extends Controller {
  static targets = [
    "input", "bottomBar", "clearBtn", "askBtn", "searchBtn",
    "searchIcon", "searchBox", "placeholder",
    "answerPanel", "answerContent", "sourcesPanel", "sourcesList",
    "loadingPanel", "noAnswerPanel", "feedbackPanel", "feedbackUp", "feedbackDown",
    "aiPanels",
  ]

  static values = {
    socketUrl: String,
    token: String,
    kbToken: String,
    timeout: { type: Number, default: 30 },
    placeholder: String,
    searchUrl: String,
  }

  connect() {
    this.expanded = false
    this.channel = null
    this.socket = null
    this.aiLogToken = ""
    this.feedbackSent = 0
  }

  disconnect() {
    if (this.channel) this.channel.leave()
    if (this.socket) this.socket.disconnect()
  }

  // --- Akcje z HTML ---

  onInput() {
    this._autoResize()
    this._updateButtons()
  }

  onFocus() {
    if (!this.expanded) {
      this.expanded = true
      // Przełącz searchBox na absolute — nasunie się na elementy poniżej bez skakania layoutu
      if (this.hasSearchBoxTarget) {
        this.searchBoxTarget.classList.add("absolute", "left-0", "right-0", "z-50", "shadow-lg")
        this.searchBoxTarget.classList.remove("shadow-xs")
      }
      this.inputTarget.rows = 2
      this.inputTarget.style.height = "auto"
      if (this.hasBottomBarTarget) this.bottomBarTarget.classList.remove("hidden")
    }
    this._updateButtons()
  }

  onBlur() {
    setTimeout(() => {
      const q = this.inputTarget.value.trim()
      if (!q) this._collapse()
    }, 200)
  }

  clear(event) {
    if (event) event.preventDefault()
    this.inputTarget.value = ""
    this.hideAllAiPanels()
    this._updateButtons()
    this.inputTarget.focus()
  }

  // Przycisk "Szukaj" — submit formularza (pełne wyniki ES)
  doSearch(event) {
    if (event) event.preventDefault()
    const q = this.inputTarget.value.trim()
    if (q.length < 2) return
    // Submit formularza GET
    this.inputTarget.closest("form").submit()
  }

  // Przycisk "Zapytaj" — AI WebSocket
  async doAsk(event) {
    if (event) event.preventDefault()
    const question = this.inputTarget.value.trim()
    if (!question || question.length < 3) return

    this.showAiLoading()
    this._setButtonsDisabled(true)
    this.aiLogToken = ""
    this.feedbackSent = 0

    try {
      await this._logAiCreate(question)
      const channel = await this._ensureChannel()

      const result = await new Promise((resolve) => {
        let settled = false
        const done = (p) => { if (settled) return; settled = true; channel.off("answer", aR); channel.off("error", eR); resolve(p) }
        const aR = channel.on("answer", done)
        const eR = channel.on("error", (p) => done({ answer: null, error: p.message }))
        setTimeout(() => done({ answer: null, timeout: true }), this.timeoutValue * 1000)
        channel.push("ask", { question })
      })

      this._logAiUpdate(result)

      if (result.answer && !result.no_answer) {
        this.showAiAnswer(result.answer, result.context_meta || result.context || [])
      } else {
        this.showAiNoAnswer()
      }
    } catch (e) {
      console.warn("KB AI ask failed:", e)
      this.showAiNoAnswer()
    }
    this._setButtonsDisabled(false)
  }

  feedbackHelpful() {
    if (this.feedbackSent !== 0) return
    this.feedbackSent = 1
    this._sendFeedback(1)
    if (this.hasFeedbackUpTarget) {
      this.feedbackUpTarget.classList.add("text-green-600")
      this.feedbackUpTarget.classList.remove("text-gray-400")
    }
  }

  feedbackNotHelpful() {
    if (this.feedbackSent !== 0) return
    this.feedbackSent = -1
    this._sendFeedback(-1)
    if (this.hasFeedbackDownTarget) {
      this.feedbackDownTarget.classList.add("text-red-600")
      this.feedbackDownTarget.classList.remove("text-gray-400")
    }
  }

  // --- Prywatne: UI ---

  _collapse() {
    this.expanded = false
    // Przywróć searchBox do normalnego flow
    if (this.hasSearchBoxTarget) {
      this.searchBoxTarget.classList.remove("absolute", "left-0", "right-0", "z-50", "shadow-lg")
      this.searchBoxTarget.classList.add("shadow-xs")
    }
    this.inputTarget.rows = 1
    this.inputTarget.style.height = "2.75rem"
    if (this.hasBottomBarTarget) this.bottomBarTarget.classList.add("hidden")
  }

  _autoResize() {
    if (!this.expanded) return
    const el = this.inputTarget
    el.style.height = "auto"
    el.style.height = Math.min(el.scrollHeight, 120) + "px"
  }

  _updateButtons() {
    const q = this.inputTarget.value.trim()
    const hasText = q.length >= 3
    if (this.hasClearBtnTarget) this.clearBtnTarget.classList.toggle("hidden", !q)
    if (this.hasAskBtnTarget) this.askBtnTarget.disabled = !hasText
    if (this.hasSearchBtnTarget) this.searchBtnTarget.disabled = !hasText
  }

  showAiLoading() {
    this.hideAllAiPanels()
    this._positionAiPanels()
    if (this.hasLoadingPanelTarget) this.loadingPanelTarget.classList.remove("hidden")
  }

  _positionAiPanels() {
    if (!this.hasAiPanelsTarget || !this.hasSearchBoxTarget) return
    const boxH = this.searchBoxTarget.offsetHeight
    this.aiPanelsTarget.style.top = boxH + "px"
  }

  showAiAnswer(answer, sources) {
    this.hideAllAiPanels()
    if (this.hasAnswerPanelTarget) this.answerPanelTarget.classList.remove("hidden")
    if (this.hasAnswerContentTarget) this.answerContentTarget.innerHTML = simpleMarkdownToHtml(answer)
    if (this.hasFeedbackPanelTarget) this.feedbackPanelTarget.classList.remove("hidden")

    // Źródła — na razie nie pokazujemy (context_meta nie zawiera tytułów)
  }

  showAiNoAnswer() {
    this.hideAllAiPanels()
    if (this.hasNoAnswerPanelTarget) this.noAnswerPanelTarget.classList.remove("hidden")
  }

  hideAllAiPanels() {
    if (this.hasAnswerPanelTarget) this.answerPanelTarget.classList.add("hidden")
    if (this.hasLoadingPanelTarget) this.loadingPanelTarget.classList.add("hidden")
    if (this.hasNoAnswerPanelTarget) this.noAnswerPanelTarget.classList.add("hidden")
    if (this.hasSourcesPanelTarget) this.sourcesPanelTarget.classList.add("hidden")
    if (this.hasFeedbackPanelTarget) this.feedbackPanelTarget.classList.add("hidden")
    if (this.hasFeedbackUpTarget) {
      this.feedbackUpTarget.classList.remove("text-green-600")
      this.feedbackUpTarget.classList.add("text-gray-400")
    }
    if (this.hasFeedbackDownTarget) {
      this.feedbackDownTarget.classList.remove("text-red-600")
      this.feedbackDownTarget.classList.add("text-gray-400")
    }
  }

  // --- Prywatne: WebSocket & API ---

  _ensureChannel() {
    return new Promise((resolve, reject) => {
      if (this.channel) { resolve(this.channel); return }
      if (!this.socketUrlValue || !this.tokenValue) { reject(new Error("Brak konfiguracji AI WebSocket")); return }

      let settled = false
      const fail = (msg) => { if (settled) return; settled = true; this.channel = null; if (this.socket) { this.socket.disconnect(); this.socket = null }; reject(new Error(msg)) }

      this.socket = new Socket(this.socketUrlValue, { params: { token: this.tokenValue } })
      this.socket.onError(() => fail("WebSocket connection failed"))
      this.socket.connect()
      this.channel = this.socket.channel("noe:widget_ai:lobby", {})
      this.channel.join()
        .receive("ok", () => { if (!settled) { settled = true; resolve(this.channel) } })
        .receive("error", (resp) => fail(`Join error: ${JSON.stringify(resp)}`))
      setTimeout(() => fail("WebSocket connection timeout"), 8000)
    })
  }

  async _logAiCreate(question) {
    try {
      const res = await fetch(`/kb/widget/ai_log?kb_token=${this.kbTokenValue}&source_type=kb_view_ai`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question }),
      })
      if (res.ok) { const data = await res.json(); this.aiLogToken = data.log_token || "" }
    } catch (_e) { /* ignoruj */ }
  }

  async _logAiUpdate(result) {
    if (!this.aiLogToken) return
    try {
      await fetch(`/kb/widget/ai_log_update?kb_token=${this.kbTokenValue}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          log_token: this.aiLogToken,
          answer: result.answer || "",
          no_answer: result.no_answer || false,
          ai_model: result.model,
          provider: result.provider,
          context_count: result.context_count,
          context_meta: result.context_meta || result.context,
          duration: result.duration,
        }),
      })
    } catch (_e) { /* ignoruj */ }
  }

  _setButtonsDisabled(disabled) {
    if (this.hasAskBtnTarget) this.askBtnTarget.disabled = disabled
    if (this.hasSearchBtnTarget) this.searchBtnTarget.disabled = disabled
  }

  async _sendFeedback(feedback) {
    if (!this.aiLogToken) return
    try {
      await fetch(`/kb/widget/ai_feedback?kb_token=${this.kbTokenValue}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ log_token: this.aiLogToken, feedback }),
      })
    } catch (_e) { /* ignoruj */ }
  }
}
